import { hydrateRoot, createRoot } from 'react-dom/client';
import { ClientApp } from './App';
import { BrowserRouter } from 'react-router';
import { DehydratedState } from '@tanstack/react-query';
import axios from 'axios';
import { getSelectedTenantId } from './components/TenantSwitcher';
import { registerChunkReloadRecovery } from './utils/chunkReload';

// Accessibility widget styles are always bundled by Vite because the CDN
// index.css is only rebuilt on master merges, so new styles added in PRs
// would be missing from the PR preview environment.
import '@default-theme/Common/accessibility.scss';
import '@default-theme/Common/accessibility-widget.scss';

// In development mode, import local SCSS files for HMR support
// These will be compiled by Vite and served locally instead of from CDN
if (import.meta.env.DEV) {
  // Resolve the active tenant from the x-tenant-id cookie (defaults to npl)
  const tenantId = getSelectedTenantId() || 'npl';

  // Component styles (index.scss + breakpoints.scss). Most tenants share the
  // default-theme; the sfl tenant renders from its own sfl-theme copy. Imported
  // by relative path (like the tenant token themes below) so no Vite alias is
  // needed.
  const componentThemeImports: Record<string, () => Promise<unknown>> = {
    sfl: () =>
      Promise.all([
        import('../../../packages/themes/sfl-theme/index.scss'),
        import('../../../packages/themes/sfl-theme/breakpoints.scss'),
      ]),
  };
  const loadDefaultComponentTheme = () =>
    Promise.all([import('@default-theme/index.scss'), import('@default-theme/breakpoints.scss')]);
  (componentThemeImports[tenantId] || loadDefaultComponentTheme)();

  // Map of tenant token themes - explicit imports so Vite can process each SCSS file
  const tenantThemeImports: Record<string, () => Promise<unknown>> = {
    isthmian: () => import('../../../packages/clients/isthmian/theme.scss'),
    npl: () => import('../../../packages/clients/npl/theme.scss'),
    wnl: () => import('../../../packages/clients/wnl/theme.scss'),
    nl: () => import('../../../packages/clients/nl/theme.scss'),
    'belper-town': () => import('../../../packages/clients/belper-town/theme.scss'),
    'grimsby-borough': () => import('../../../packages/clients/grimsby-borough/theme.scss'),
    'newton-aycliffe': () => import('../../../packages/clients/newton-aycliffe/theme.scss'),
    'ashton-united': () => import('../../../packages/clients/ashton-united/theme.scss'),
    liversedge: () => import('../../../packages/clients/liversedge/theme.scss'),
    'darlaston-town': () => import('../../../packages/clients/darlaston-town/theme.scss'),
    'vauxhall-motors': () => import('../../../packages/clients/vauxhall-motors/theme.scss'),
    'corby-town': () => import('../../../packages/clients/corby-town/theme.scss'),
    'sutton-coldfield-town': () => import('../../../packages/clients/sutton-coldfield-town/theme.scss'),
    'potters-bar-united': () => import('../../../packages/clients/potters-bar-united/theme.scss'),
    'racing-club-warwick': () => import('../../../packages/clients/racing-club-warwick/theme.scss'),
    lancing: () => import('../../../packages/clients/lancing/theme.scss'),
    'witton-albion': () => import('../../../packages/clients/witton-albion/theme.scss'),
    'loughborough-students': () => import('../../../packages/clients/loughborough-students/theme.scss'),
    'hebburn-town': () => import('../../../packages/clients/hebburn-town/theme.scss'),
    'leek-town': () => import('../../../packages/clients/leek-town/theme.scss'),
    'lancaster-city': () => import('../../../packages/clients/lancaster-city/theme.scss'),
    'egham-town': () => import('../../../packages/clients/egham-town/theme.scss'),
    silsden: () => import('../../../packages/clients/silsden/theme.scss'),
    sfl: () => import('../../../packages/clients/sfl/theme.scss'),
    streamline: () => import('../../../packages/clients/streamline/theme.scss'),
  };

  // Load the token theme for the current tenant
  const themeImport = tenantThemeImports[tenantId] || tenantThemeImports['npl'];
  themeImport();
}

// Note: Theme CSS variables are already defined in the client's theme.scss
// which is compiled to ./styles/theme.css and imported in App.tsx.
// Remote theme tokens will override these when fetched via useThemeQuery.

declare global {
  interface Window {
    __REACT_QUERY_STATE__?: DehydratedState;
  }
}

// Add axios interceptor to include x-tenant-id header
axios.interceptors.request.use((config) => {
  // Get tenant ID from localStorage (if in test env) or fallback to env variable
  const tenantId = getSelectedTenantId();
  if (tenantId) {
    config.headers['x-tenant-id'] = tenantId;
  }
  return config;
});

// Registered before hydration so a *lazy route chunk* that 404s after a deploy
// triggers the bounded auto-reload. This cannot help when the entry chunk itself
// is the one missing — this module never runs in that case, and only the short
// negative-cache TTL in lib/requestGuards.mjs addresses it.
registerChunkReloadRecovery();

const rootElement = document.getElementById('root')!;
const app = (
  <BrowserRouter>
    <ClientApp />
  </BrowserRouter>
);

// Use hydrateRoot when server-rendered content exists, createRoot otherwise (dev without SSR)
if (rootElement.innerHTML.trim()) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}
